import React from 'react';
import { Calendar, MapPin, Globe } from 'lucide-react';
import { parseISO } from 'date-fns';
import { EventLocation } from '../../types/eventDetails.types';
import { formatEventDateTimeV2 } from '../../utils/formatEventDateTimeV2';
import TIMEZONES from '../../../shared/timezones';

interface EventDateLocationDisplayProps {
  startsAt?: string;
  endsAt?: string;
  showStartTime?: boolean;
  showEndTime?: boolean;
  timeZone?: string;
  location?: EventLocation;
  showDate?: boolean;
  showTime?: boolean;
  showLocation?: boolean;
  showTimezone?: boolean;
  className?: string;
  textColor?: string;
  onClick?: () => void;
}

export const EventDateLocationDisplay: React.FC<EventDateLocationDisplayProps> = ({
  startsAt,
  endsAt,
  showStartTime,
  showEndTime,
  timeZone,
  location,
  showDate = true,
  showTime = true,
  showLocation = true,
  showTimezone = false,
  className = '',
  textColor = '#ffffff',
  onClick
}) => {
  // Get the formatted date/time string using our utility
  const getFormattedDateTime = () => {
    if (!startsAt) return '';

    // If not showing date or time, return empty
    if (!showDate && !showTime) return '';

    // Use the formatting utility
    return formatEventDateTimeV2({
      startsAt,
      endsAt: endsAt || startsAt,
      showStartTime: showTime ? showStartTime : false,
      showEndTime: showTime ? showEndTime : false,
      timeZone,
      timeFormat: '12_hour',
      long: true
    });
  };

  // Format time zone for display
  const formatTimeZone = () => {
    if (!timeZone) return '';

    // First, check if the stored value matches an IANA timezone value
    const timezoneByValue = TIMEZONES.find(tz => tz.value === timeZone);
    if (timezoneByValue) {
      // Create a date to get the current timezone abbreviation
      const date = startsAt ? parseISO(startsAt) : new Date();
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezoneByValue.value,
        timeZoneName: 'short'
      });
      const parts = formatter.formatToParts(date);
      const timeZonePart = parts.find(part => part.type === 'timeZoneName');
      return timeZonePart?.value || timezoneByValue.label;
    }

    // Check if the stored value matches a label
    const timezoneByLabel = TIMEZONES.find(tz => tz.label === timeZone);
    if (timezoneByLabel) {
      // Create a date to get the current timezone abbreviation
      const date = startsAt ? parseISO(startsAt) : new Date();
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezoneByLabel.value,
        timeZoneName: 'short'
      });
      const parts = formatter.formatToParts(date);
      const timeZonePart = parts.find(part => part.type === 'timeZoneName');
      return timeZonePart?.value || timezoneByLabel.label;
    }

    // Fallback: if it's already a short format like "EST" or "PST", return as is
    if (timeZone.length <= 5) {
      return timeZone;
    }

    // Otherwise, try to extract a meaningful part
    const parts = timeZone.split('/');
    return parts[parts.length - 1].replace(/_/g, ' ');
  };

  const containerProps = onClick ? {
    onClick,
    className: `px-4 py-3 bg-black/20 backdrop-blur-md rounded-xl shadow-sm hover:bg-black/30 transition-colors cursor-pointer text-left ${className}`,
    style: { color: textColor },
    role: 'button',
    tabIndex: 0,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick();
      }
    }
  } : {
    className: `px-4 py-3 bg-black/20 backdrop-blur-md rounded-xl shadow-sm text-left ${className}`,
    style: { color: textColor }
  };

  const Container = onClick ? 'button' : 'div';

  return (
    <Container {...containerProps}>
      {/* Date and time row */}
      {(showDate || showTime) && (
        <div className="flex items-center gap-2 text-sm">
          <Calendar className="h-4 w-4 flex-shrink-0" />
          <span>
            {getFormattedDateTime()}
            {/* Show timezone inline if it's abbreviated (5 chars or less) */}
            {showTime && showTimezone && timeZone && formatTimeZone() && formatTimeZone().length <= 5 && (
              <span style={{ opacity: 0.8 }}> ({formatTimeZone()})</span>
            )}
          </span>
        </div>
      )}

      {/* Time zone row - show only if we have time displayed and it's NOT abbreviated */}
      {(showTime && showTimezone && timeZone && formatTimeZone() && formatTimeZone().length > 5) && (
        <div className={`flex items-center gap-2 text-sm ${(showDate || showTime) ? 'mt-2' : ''}`}>
          <Globe className="h-4 w-4 flex-shrink-0" />
          <span>{formatTimeZone()}</span>
        </div>
      )}

      {/* Location row */}
      {showLocation && location && (
        <div className={`flex items-center gap-2 text-sm ${(showDate || showTime || (showTimezone && timeZone && formatTimeZone().length > 5)) ? 'mt-2' : ''}`}>
          <MapPin className="h-4 w-4 flex-shrink-0" />
          <span>
            {location.cityState ||
             (location.city && location.state ? `${location.city}, ${location.state}` :
              location.name || '')}
          </span>
        </div>
      )}
    </Container>
  );
};